import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { CustomEventService } from '@shared/services/custom-event.service';
import { ProjectService } from '@shared/services/project.service';

import { searchFieldsOnTable } from '@shared/utils/search.util';
import { sortFieldsOnTable } from '@shared/utils/sort.util';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { commonChartTooltip } from '@shared/variables/echart-common.variable';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EChartOption } from 'echarts';
import { fromJS, Map } from 'immutable';

@UntilDestroy()
@Component({
  selector: 'app-project-card',
  templateUrl: './project-card.component.html'
})
export class ProjectCardComponent implements OnInit {

  PAGE_SIZE = 10;

  loading: boolean;
  loadingByDate: boolean;
  projectId: string;
  projectData: Map<string, any> = Map();

  chartOptionMap: Map<string, EChartOption> = Map();
  projectDetailData: Map<string, any> = Map();
  developersScore = [];

  searchValue = '';

  constructor(
    private customEventService: CustomEventService,
    private projectService: ProjectService,
    private route: ActivatedRoute
  ) {
    route.params.pipe(
      untilDestroyed(this)
    ).subscribe(response => {
      this.projectId = response.id;
    });
  }

  ngOnInit(): void {
    this.getCardList();
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(
      ({ action, data }) => {
        if (action === ActionTypesEnum.UPDATE_DURATIONS_DATE) {
          this.updateData(data);
        }
      });
  }

  sort(e): void {
    const data = this.projectDetailData.getIn(['DEVELOPERS_SCORE', 'data']).toJS();

    if (!e.value && !this.searchValue) {
      this.developersScore = data;
      return;
    }

    this.developersScore = sortFieldsOnTable(this.developersScore, e);
  }

  search(): void {
    const data = this.projectDetailData.getIn(['DEVELOPERS_SCORE', 'data']).toJS();

    if (!this.searchValue.length) {
      this.developersScore = data;
      return;
    }
    const fields = [ 'name' ];

    this.developersScore = searchFieldsOnTable(data, this.searchValue, fields);
  }

  reset(): void {
    this.searchValue = '';
    this.search();
  }

  getCardList(date?: any): void {
    if(!date){
      this.loading = true;
    } else {
      this.loadingByDate = true;
    }
    this.projectService.get(this.projectId).pipe(
      untilDestroyed(this)
    ).subscribe((response: any) => {
      this.projectData = fromJS(response);

      if (this.projectData.has('widgets') && this.projectData.get('widgets').size){
        this.projectData.get('widgets').forEach(item => {
          this.projectDetailData = this.projectDetailData
            .setIn([item.get('type'), 'initialLoading'], true)
            .setIn([item.get('type'), 'header'], item.getIn(['header']));
          this.getWidgets(item.get('type'), date);
        });
      }

      this.loading = false;
      this.loadingByDate = false;
    }, error => {
      this.loading = false;
      this.loadingByDate = false;

      console.log('error occurred while fetching initial data for project card', error);
    });
  }


  getWidgets(type: any, date: any) {
    this.projectService.getResult(this.projectId, type, date)
      .pipe(untilDestroyed(this)).subscribe(response => {
      this.projectDetailData = this.projectDetailData.update(type, item => item
        .remove('data')
        .mergeDeep(fromJS(response)));

      if (type === 'CODE_QUALITY'
        || type === 'PRODUCTION_METRICS') {
        this.loadChartData(this.projectDetailData.getIn([type, 'data']), type);
      }

      if (this.projectDetailData.hasIn(['DEVELOPERS_SCORE','data'])) {
        this.developersScore = this.projectDetailData
          .getIn([
            'DEVELOPERS_SCORE',
            'data'
          ])
          .toJS()
          .sort((a, b) =>
            a.issues < b.issues ? 1 : b.issues < a.issues ? -1 : 0
          );
      }

      this.projectDetailData = this.projectDetailData.updateIn([type, 'initialLoading'], _ => false);
    }, error => {
      this.projectDetailData = this.projectDetailData.updateIn([type, 'initialLoading'], _ => false);
      console.log('error occurred while fetching widgets data for scorecard', error);
    });
  }

  loadChartData(chartData, type: string): void {
    const genarateChart = (data): EChartOption => {
      return {
        grid: {
          top: '20px',
          bottom: '20px',
          left: 0,
          right: 0
        },
        yAxis: {
          type: 'value',
          show: false,
          data: data.data
        },
        xAxis: {
          type: 'category',
          show: false,
          data: data.xAxis
        },
        tooltip: {
          ...commonChartTooltip,
          trigger: 'axis',
          formatter: `{c}<br>{b}`,
        },
        series: [
          {
            smooth: true,
            showSymbol: false,
            data: data.data,
            type: 'line',
            areaStyle: {
              color: '#eef9ff'
            },
            itemStyle: {
              normal: {
                color: '#4fc4ff'
              }
            },
            lineStyle: {
              width: 2,
              color: '#4fc4ff'
            }
          }
        ]
      }
    };

    chartData.toJS().forEach((item, index) => {
      this.chartOptionMap = this.chartOptionMap.setIn([type, index], genarateChart(item.chart));
    });
  }

  updateData(date: any): void {
    this.getCardList(date);
  }
}
